import React, { useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";

const blank = { secret_reference: "ENTRA_CLIENT_CERT_KEY_B64", secret_version: "", overlap_start: "", overlap_end: "", notes: "", template_id: "" };

const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export function StageCertificateDialog({ open, onClose, onStaged, templates = [] }) {
  const [form, setForm] = useState(blank);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (key) => (event) => setForm({ ...form, [key]: event.target.value });
  const ready = file && form.secret_reference && form.secret_version && form.overlap_start && form.overlap_end;

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/sso/records", {
        ...form,
        certificate_b64: await readAsBase64(file),
        overlap_start: new Date(form.overlap_start).toISOString(),
        overlap_end: new Date(form.overlap_end).toISOString(),
      });
      toast.success("Certificate staged for rotation");
      setForm(blank);
      setFile(null);
      onStaged();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="stage-cert-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Stage a certificate rotation</DialogTitle>
            <DialogDescription>
              Upload the PUBLIC certificate only (.cer or .pem). Never upload a private key, PFX,
              client secret or vault value — those stay in secure server settings.
            </DialogDescription>
          </DialogHeader>
          <label className="block">
            <span className="field-label">Public certificate</span>
            <span className="btn-ghost flex items-center gap-2 text-sm cursor-pointer">
              <Upload size={14} /> {file ? file.name : "Choose .cer or .pem file"}
              <input type="file" accept=".cer,.crt,.pem" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="stage-cert-file" />
            </span>
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block"><span className="field-label">Backend secret reference</span>
              <input className="input-flat" value={form.secret_reference} onChange={set("secret_reference")} data-testid="stage-cert-secret-ref" /></label>
            <label className="block"><span className="field-label">Approved secret version</span>
              <input className="input-flat" value={form.secret_version} onChange={set("secret_version")} placeholder="e.g. v2" data-testid="stage-cert-secret-version" /></label>
            <label className="block"><span className="field-label">Overlap starts</span>
              <input type="datetime-local" className="input-flat" value={form.overlap_start} onChange={set("overlap_start")} data-testid="stage-cert-overlap-start" /></label>
            <label className="block"><span className="field-label">Overlap ends</span>
              <input type="datetime-local" className="input-flat" value={form.overlap_end} onChange={set("overlap_end")} data-testid="stage-cert-overlap-end" /></label>
          </div>
          <label className="block"><span className="field-label">Rotation template</span>
            <select className="input-flat" value={form.template_id} onChange={set("template_id")} data-testid="stage-cert-template">
              <option value="">No template</option>
              {templates.map((template) => <option key={template.id} value={template.id}>{`${template.title} (${template.steps.length} steps)`}</option>)}
            </select></label>
          <label className="block"><span className="field-label">Notes (optional)</span>
            <textarea className="input-flat" rows={2} maxLength={500} value={form.notes} onChange={set("notes")} data-testid="stage-cert-notes" /></label>
          <DialogFooter>
            <button type="button" className="btn-ghost" onClick={onClose} data-testid="stage-cert-cancel">Cancel</button>
            <button type="submit" className="btn-primary" disabled={busy || !ready} data-testid="stage-cert-submit">{busy ? "Staging…" : "Stage rotation"}</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
